// src/features/affiliate/CampaignFormDialog.tsx
// Create a campaign for an offer (partner) or edit an existing one.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateCampaign, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, FormRow } from '@/components/form';
import { useScope } from '@/hooks/useScope';
import type { Campaign } from '@core/types/affiliate';
import { errMessage, makeEntity } from './format';

interface CampaignFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present when creating a campaign from an offer. */
  offerId?: string;
  affiliateId?: string;
  /** Present when editing an existing campaign. */
  campaign?: Campaign | null;
  onSaved: (campaign: Campaign) => void;
}

export const CampaignFormDialog = observer(({ open, onClose, offerId, affiliateId, campaign, onSaved }: CampaignFormDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useAffiliateCampaign();
  const isEdit = !!campaign;

  const [name, setName] = useState('');
  const [trafficSource, setTrafficSource] = useState('');
  const [sub1, setSub1] = useState('');
  const [sub2, setSub2] = useState('');
  const [sub3, setSub3] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(campaign?.name ?? '');
    setTrafficSource(campaign?.traffic_source ?? '');
    setSub1(campaign?.sub_id_1 ?? '');
    setSub2(campaign?.sub_id_2 ?? '');
    setSub3(campaign?.sub_id_3 ?? '');
  }, [open, campaign]);

  const dirty = !!(name.trim() || trafficSource.trim());

  const save = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && campaign?.entity) {
        const saved = await store.UpdateCampaign(campaign.entity.id, {
          entity: campaign.entity,
          update_mask: ['name', 'traffic_source', 'sub_id_1', 'sub_id_2', 'sub_id_3'],
          name: name.trim(),
          traffic_source: trafficSource,
          sub_id_1: sub1,
          sub_id_2: sub2,
          sub_id_3: sub3,
          metadata: campaign.metadata ?? {},
        });
        onSaved(saved);
      } else {
        const saved = await store.CreateCampaign({
          entity: makeEntity('', 'campaign', scope),
          name: name.trim(),
          offer_id: offerId ?? '',
          affiliate_id: affiliateId ?? '',
          traffic_source: trafficSource,
          sub_id_1: sub1,
          sub_id_2: sub2,
          sub_id_3: sub3,
          metadata: {},
        });
        onSaved(saved);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t('affiliate.campaigns.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={save}
      submitLabel={isEdit ? t('common.saveChanges') : t('common.create')}
      canSubmit={!!name.trim()}
      isSubmitting={isSaving}
      dirty={dirty}
      width="md"
      aria-label={isEdit ? t('affiliate.campaigns.editTitle') : t('affiliate.campaigns.createTitle')}
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={`${t('affiliate.campaigns.name')}…`}
          initialFocus
          aria-label={t('affiliate.campaigns.name')}
        />
      </div>
      <div className="space-y-1 px-3.5 pb-3">
        <FormRow label={t('affiliate.campaigns.trafficSource')} labelWidth="sm">
          <input
            value={trafficSource}
            onChange={(e) => setTrafficSource(e.target.value)}
            placeholder={t('affiliate.campaigns.trafficSourcePlaceholder')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('affiliate.campaigns.subIds')} labelWidth="sm">
          <div className="grid grid-cols-3 gap-2">
            <input value={sub1} onChange={(e) => setSub1(e.target.value)} placeholder="sub_id_1" aria-label="Sub ID 1" className="rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-ring" />
            <input value={sub2} onChange={(e) => setSub2(e.target.value)} placeholder="sub_id_2" aria-label="Sub ID 2" className="rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-ring" />
            <input value={sub3} onChange={(e) => setSub3(e.target.value)} placeholder="sub_id_3" aria-label="Sub ID 3" className="rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </FormRow>
      </div>
    </FormDialog>
  );
});
